import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { addDays, isAfter } from 'date-fns';
import { BehaviorSubject, Observable, combineLatest, interval, merge, timer } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { ConfigService } from '@mona/config';
import { DailyGoal, DataUpdateMessage, DataUpdateMessageOperation } from '@mona/models';
import { ChangeLogService } from '@mona/pdms/data-access-changelog';
import { applyLastChangedByToPrescriptions } from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { DailyGoalsState } from '../entities';
import { activeDayStart, splitDailyGoals } from '../infrastructure/helpers/split-daily-goals';
import { DailyGoalsSelectors } from '../state';
import { DailyGoalsActions } from '../state/actions/daily-goals.actions';
import { selectDailyGoalAll, selectDailyGoalsEntities } from '../state/selectors/daily-goals.selectors';

/**
 * Notifications store service
 */
@Injectable({ providedIn: 'root' })
export class DataAccessDailyGoalsFacade {
    /** Forces active/inactive to recompute without clearing the store. */
    private readonly refresh$ = new BehaviorSubject(0);

    readonly dailyGoals$: Observable<{ active: DailyGoal[]; inactive: DailyGoal[] }> = combineLatest([
        this.store.select(selectDailyGoalAll),
        this.refresh$,
    ]).pipe(map(([goals]) => splitDailyGoals(goals, this.getGoalsResetDate())));

    readonly dailyGoalsMap$: Observable<Record<string, DailyGoal>> = this.store.select(selectDailyGoalsEntities);

    readonly isLoading$ = this.store.select(DailyGoalsSelectors.selectIsLoading);

    private resetAt: Date | undefined;
    private resetDay: number | undefined;

    constructor(
        private store: Store<DailyGoalsState>,
        private changeLogService: ChangeLogService,
        private configService: ConfigService,
        private practitionersFacade: PractitionersFacade,
    ) {}

    async loadDailyGoalsData(debounceTime = 0): Promise<void> {
        // NOTE: API just don't have the freshest data without any delays
        await new Promise(resolve => setTimeout(resolve, debounceTime));
        this.store.dispatch(DailyGoalsActions.loadDailyGoals());
    }

    /**
     * Local-day reset instant from balanceCalculationResetTime (MD-11673).
     * Shared by subtitle, active/inactive split, and reset timer.
     */
    getGoalsResetDate(): Date {
        const now = new Date();
        const day = now.getDate();

        if (!this.resetAt || this.resetDay !== day) {
            const time = this.configService.get('balanceCalculationResetTime');
            const hour = time ? +time.split(':')[0] : 5;
            this.resetAt = new Date(new Date().setHours(hour, 0, 0, 0));
            this.resetDay = day;
        }
        return this.resetAt;
    }

    /** Re-split active/inactive; keeps goals in the store so Inactive can show them. */
    refreshSplit(): void {
        this.resetAt = undefined;
        this.resetDay = undefined;
        this.refresh$.next(this.refresh$.value + 1);
    }

    /**
     * While task list is open: refresh split at next reset (+ every 24h).
     * 60s poll covers Electron delaying a long one-shot timer.
     */
    scheduleReset(until$: Observable<unknown>): void {
        const resetAt = this.getGoalsResetDate();
        const nextReset = isAfter(new Date(), resetAt) ? addDays(resetAt, 1) : resetAt;
        const delay = Math.max(0, nextReset.getTime() - Date.now());

        let day = activeDayStart(this.getGoalsResetDate()).toISOString();

        merge(timer(delay, 24 * 60 * 60 * 1000), interval(60_000))
            .pipe(takeUntil(until$))
            .subscribe(() => {
                const next = activeDayStart(this.getGoalsResetDate()).toISOString();
                if (next === day) {
                    return;
                }
                day = next;
                this.refreshSplit();
            });
    }

    createDailyGoal(goal: Partial<DailyGoal>): void {
        this.changeLogService.createDailyGoal(goal);
    }

    updateDailyGoal(goal: Partial<DailyGoal>): void {
        this.changeLogService.updateDailyGoal(goal);
    }

    removeDailyGoal(id: string): void {
        this.changeLogService.removeDailyGoal(id);
    }

    clearDailyGoals(): void {
        this.store.dispatch(DailyGoalsActions.clearDailyGoals());
    }

    handleDailyGoalDataUpdateMessage(updateMessage: DataUpdateMessage<DailyGoal>): void {
        if (updateMessage.operation === DataUpdateMessageOperation.Delete) {
            this.store.dispatch(DailyGoalsActions.removeDailyGoals({ ids: [updateMessage.modelId] }));
            return;
        }

        this.practitionersFacade.practitionersMap$.pipe(take(1)).subscribe(practitionersMap => {
            const data = applyLastChangedByToPrescriptions(
                [{ ...updateMessage.payload, id: updateMessage.modelId } as DailyGoal],
                practitionersMap,
            ) as DailyGoal[];
            this.store.dispatch(DailyGoalsActions.upsertDailyGoals({ data }));
        });
    }
}
